"use client"

import styled from "@emotion/styled"
import {
  GetMarketingBannersQuery,
  MarketingBannerItem,
} from "@eurodrive/apollo"
import { extractProperty, getNonUmbracoUrl } from "@eurodrive/utils"
import { getUmbracoMediaUrl } from "@eurodrive/utils/getUmbracoMediaUrl"
import { promoBannerClicked } from "@eurodrive/utils/gtm"
import { Box, Button, IconButton, Typography } from "@mui/material"
import Image from "next/image"
import Link from "next/link"
import { useState } from "react"
import { useInView } from "react-intersection-observer"
import Container from "./Container"
import ReactPlayerComponent, { isVideo } from "./ReactPlayer"
import { TransitionUp } from "./transitions/TransitionUp"
import { useInViewConfig } from "./transitions/utils"

const StyledLayout = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 609px;
  position: relative;
`

const GradientOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 100%);
  pointer-events: none;
  z-index: 1;
`

const InnerLayout = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: flex-end;
  ${({ theme }) => theme.breakpoints.down("md")} {
    align-items: center;
    text-align: center;
  }
`

const ControlsBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  height: 100%;
  padding: 64px 0;
  position: relative;
  z-index: 2;
`

const TextBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 427px;
`

const StyledButton = styled(Button)`
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 249px;
  }
`

const StyeldIconButton = styled(IconButton)`
  min-width: 0;
  padding: 0;
  z-index: 100;
  :focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`
export default function Marketing({
  data,
  marketing,
}: {
  data: any
  marketing: GetMarketingBannersQuery
}) {
  const { ref: inViewRef, inView } = useInView(useInViewConfig)

  // Extract properties from the block data
  const banner = extractProperty(data, "banner")
  const url = banner?.[0]?.url || data?.[0]?.url
  const marketingBanners =
    marketing?.components?.edges?.map((el) => {
      const object = el.node
      const properties = object?.namedProperties as MarketingBannerItem
      return {
        url: object?.url,
        title: properties?.title?.value,
        image: properties?.media?.mediaItems?.[0]?.url,
        altText: properties?.media?.mediaItems?.[0]?.altText,
        subtitle: properties?.subtitle?.value,
        boldText: properties?.boldText?.value,
        button: properties?.button?.links?.[0]?.url,
        buttonText: properties?.button?.links?.[0]?.name,
      }
    }) || []
  const selectedBanner = marketingBanners.find((el) => url === el.url)
  if (!selectedBanner) {
    return null
  }
  const isVideoUrl = isVideo(selectedBanner.image || "")
  const [playing, setPlaying] = useState(false)
  const handleResume = () => {
    setPlaying(true)
  }
  const handlePause = () => {
    setPlaying(false)
  }

  const handleBannerClick = () => {
    promoBannerClicked(
      selectedBanner.title || "marketing_banner",
      url || "unknown",
    )
  }

  return (
    <StyledLayout ref={inViewRef}>
      {isVideoUrl ? (
        <ReactPlayerComponent
          playing={playing}
          source={getUmbracoMediaUrl(selectedBanner.image || "")}
          setPlaying={setPlaying}
          volume={false}
          setVolume={() => {}}
        />
      ) : (
        <>
          <Image
            src={getUmbracoMediaUrl(selectedBanner.image || "")}
            alt={selectedBanner.altText || ""}
            style={{
              objectFit: "cover",
            }}
            fill
          />
        </>
      )}

      <GradientOverlay />
      <Container heightFull paddingMobile>
        <ControlsBox>
          <TransitionUp $inView={inView}>
            <InnerLayout>
              <TextBox>
                <Typography variant="h2" color="white" fontWeight={400}>
                  {selectedBanner.title}
                </Typography>
                <Typography variant="body2" color="white">
                  {selectedBanner.subtitle}
                </Typography>
                <Typography variant="h3" color="white" component={"p"}>
                  {selectedBanner.boldText}
                </Typography>
              </TextBox>
              <StyledButton
                variant="primary"
                LinkComponent={Link}
                title={selectedBanner.title}
                size="large"
                href={getNonUmbracoUrl(selectedBanner.button || "")}
                onClick={handleBannerClick}
              >
                {selectedBanner.buttonText}
              </StyledButton>
            </InnerLayout>
            {isVideoUrl ? (
              <>
                {!playing ? (
                  <StyeldIconButton onClick={handleResume}>
                    <Image
                      src={"/icons/play-white.svg"}
                      width={30}
                      height={30}
                      alt="play"
                    />
                  </StyeldIconButton>
                ) : (
                  <StyeldIconButton onClick={handlePause}>
                    <Image
                      src={"/icons/pause-white.svg"}
                      width={30}
                      height={30}
                      alt="pause"
                    />
                  </StyeldIconButton>
                )}
              </>
            ) : null}
          </TransitionUp>
        </ControlsBox>
      </Container>
      {/* Marketing Content */}
    </StyledLayout>
  )
}
